'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { FiltersSection, OffersTable, ContentPreviewModal, StartCampaignModal } from './components';
import { OffersFilters, ModelOption, RevshareOffer, OwnerCounts } from './types';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import partnerRevshareService, {
  RevshareOffersRequestParams,
  RevshareOffersSortBy,
} from '../../../api/partner/partnerRevshareService';
import { showError } from '../../../utils/toast';

const RevshareOffers: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard') || 'Главная', href: '/partner/dashboard' },
      { label: 'Revshare', href: '/partner/revshare/offers' },
      { label: t('partner.revshare.offers.title') || 'Офферы', isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [offers, setOffers] = useState<RevshareOffer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filters
  const [filters, setFilters] = useState<OffersFilters>(DEFAULT_FILTERS);
  const [modelSearchQuery, setModelSearchQuery] = useState('');

  // Modal state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isStartCampaignModalOpen, setIsStartCampaignModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<RevshareOffer | null>(null);

  // Available models for filter (loaded from API)
  const [availableModels, setAvailableModels] = useState<ModelOption[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Owner counts for filter labels
  const [ownerCounts, setOwnerCounts] = useState<OwnerCounts>({ creator: 0, agency: 0 });

  // Load available models from API
  const fetchAvailableModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await partnerRevshareService.getCompaniesModels({
        page: 1,
        page_size: 50,
        search: search || undefined,
      });
      if (response.objects) {
        setAvailableModels(
          response.objects.map((acc) => ({
            id: acc._id,
            name: `${String(acc.of_user_id).slice(-4)}`,
            username: acc.of_user_name || '',
            avatar: acc.avatar || '',
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Fetch owner counts for filter labels
  const fetchOwnerCounts = useCallback(async () => {
    try {
      // Fetch counts in parallel with page_size=1 to get just the count
      const [creatorResponse, agencyResponse] = await Promise.all([
        partnerRevshareService.getOffers({ page: 1, page_size: 1, owner_type: 'creator' }),
        partnerRevshareService.getOffers({ page: 1, page_size: 1, owner_type: 'agency' }),
      ]);

      setOwnerCounts({
        creator: creatorResponse.count || 0,
        agency: agencyResponse.count || 0,
      });
    } catch (error) {
      console.error('Error fetching owner counts:', error);
    }
  }, []);

  // Initial load of models and owner counts
  useEffect(() => {
    fetchAvailableModels();
    fetchOwnerCounts();
  }, [fetchAvailableModels, fetchOwnerCounts]);

  // Handle model search change - fetch from API
  const handleModelSearchChange = useCallback(
    (value: string) => {
      setModelSearchQuery(value);
      fetchAvailableModels(value);
    },
    [fetchAvailableModels]
  );

  // Check if filters are active (excluding default sort)
  const hasActiveFilters = useMemo(() => {
    return (
      filters.type !== '' ||
      filters.owner !== '' ||
      filters.model !== '' ||
      filters.source !== '' ||
      filters.sortBy !== '' ||
      (filters.price !== '' && filters.price !== 'any') ||
      filters.topCreators !== '' ||
      filters.offerCommission !== '' ||
      filters.guaranteed === true ||
      filters.allowedSource !== '' ||
      filters.excludedSource !== ''
    );
  }, [filters]);

  // Fetch offers
  const fetchOffers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: RevshareOffersRequestParams = {
        page: currentPage,
        page_size: pageSize,
      };

      // Sort - default to newest first
      const sortBy = filters.sortBy?.trim() || 'created_at';
      params.sort_by = sortBy as RevshareOffersSortBy;

      // Type filter (from "Оффер" dropdown or "Стоимость" in account popover)
      // Price filter takes priority if set
      if (filters.price && filters.price !== 'any') {
        params.type = filters.price as 'free' | 'free trial';
      } else if (filters.type) {
        params.type = filters.type as 'free' | 'free trial';
      }

      // Owner type filter
      if (filters.owner) {
        params.owner_type = filters.owner as 'creator' | 'agency';
      }

      // Old commission filter (range-based) - replaced by offerCommission
      // if (filters.commission) {
      //   const commissionRanges: Record<string, number> = {
      //     '0-20': 0,
      //     '20-30': 20,
      //     '30-40': 30,
      //     '40+': 40,
      //   };
      //   params.commission = commissionRanges[filters.commission] || 0;
      // }

      // Source filter - filter offers where this source is allowed (not in prohibited_sources)
      if (filters.source) {
        params.available_source_in = filters.source;
      }

      // Model filter
      if (filters.model) {
        params.model_id = filters.model;
      }

      // Top creators filter (performer_top in API)
      if (filters.topCreators) {
        params.performer_top = parseInt(filters.topCreators, 10);
      }

      // Offer commission filter (from offer popover)
      if (filters.offerCommission) {
        params.commission = parseInt(filters.offerCommission, 10);
      }

      // Guaranteed filter
      if (filters.guaranteed === true) {
        params.guaranteed = true;
      }

      // Resources filter - allowed source
      if (filters.allowedSource) {
        params.available_source_in = filters.allowedSource;
      }

      // Resources filter - excluded source
      if (filters.excludedSource) {
        params.available_source_out = filters.excludedSource;
      }

      const response = await partnerRevshareService.getOffers(params);

      if (response && response.objects) {
        // Normalize offers data
        const normalizedOffers = response.objects.map(normalizeOffer);
        setOffers(normalizedOffers);
        setTotalCount(response.count || 0);
      } else {
        setOffers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      showError(t('partner.revshare.offers.errors.loadFailed') || 'Не удалось загрузить офферы');
      setOffers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters, t]);

  // Normalize offer data from API
  const normalizeOffer = (offer: any): RevshareOffer => {
    const ofData = offer?.of_data || {};
    const offerData = offer?.offer || {};

    return {
      _id: offer?._id || offer?.id,
      of_user_id: offer?.of_user_id ?? 0,
      of_user_name: offer?.of_user_name ?? '',
      name: offer?.name ?? '',
      avatar: offer?.avatar,
      type: offer?.type || 'FREE',
      status: offer?.status,
      created_at: offer?.created_at,
      of_data: {
        subscribers: ofData?.subscribers ?? 0,
        overall_popularity_percent: ofData?.overall_popularity_percent ?? 0,
        likes: ofData?.likes ?? 0,
        posts: ofData?.posts ?? 0,
        favorited: ofData?.favorited ?? 0,
        blocked_countries: ofData?.blocked_countries ?? [],
        arpu: ofData?.arpu,
        conversion: ofData?.conversion,
      },
      owner:
        typeof offer?.owner === 'string'
          ? { _id: offer.owner, name: '', type: 'creator' as const }
          : offer?.owner || { _id: '', name: '', type: 'creator' as const },
      offer: offerData
        ? {
            commission: offerData?.commission ?? 0,
            prohibited_sources: offerData?.prohibited_sources ?? [],
            show_service_info_for_partner: offerData?.show_service_info_for_partner ?? false,
            custom_media_for_partner: offerData?.custom_media_for_partner ?? false,
            exist_chatters: offerData?.exist_chatters ?? false,
            chat_processing: offerData?.chat_processing,
            free_trial_access: offerData?.free_trial_access ?? false,
            description: offerData?.description,
            contact_for_content: offerData?.contact_for_content,
            status: offerData?.status || 'active',
          }
        : undefined,
      revshare_campaigns_count: offer?.revshare_campaigns_count ?? 0,
      // Active campaigns by source
      ref_share_active_by_source: offer?.ref_share_active_by_source || {},
      ref_share_active_total: offer?.ref_share_active_total ?? 0,
      sfs_posts_level: offer?.sfs_posts_level || 'None',
      // Public content
      public_content: offer?.public_content ?? [],
      // Granted (deposit info)
      granted: offer?.granted
        ? {
            need: offer.granted.need ?? 200,
            dep: offer.granted.dep ?? null,
          }
        : undefined,
      // Backward compatibility
      subscribers: ofData?.subscribers ?? offer?.subscribers ?? 0,
      overall_popularity_percent:
        ofData?.overall_popularity_percent ?? offer?.overall_popularity_percent ?? 0,
      likes: ofData?.likes ?? offer?.likes ?? 0,
      posts: ofData?.posts ?? offer?.posts ?? 0,
      favorited: ofData?.favorited ?? offer?.favorited ?? 0,
    };
  };

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFilterChange = useCallback((key: keyof OffersFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setModelSearchQuery('');
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleShowContent = useCallback((offer: RevshareOffer) => {
    setSelectedOffer(offer);
    setIsPreviewModalOpen(true);
  }, []);

  const handleStartCampaign = useCallback((offer: RevshareOffer) => {
    setSelectedOffer(offer);
    setIsStartCampaignModalOpen(true);
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
    setSelectedOffer(null);
  }, []);

  const handleCloseStartCampaignModal = useCallback(() => {
    setIsStartCampaignModalOpen(false);
    setSelectedOffer(null);
  }, []);

  return (
    <div className="w-full px-4 py-6 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <Typography variant="h4" className="mb-2">
          {t('partner.revshare.offers.title')}
        </Typography>
      </div>

      {/* Filters */}
      <FiltersSection
        filters={filters}
        availableModels={availableModels}
        isLoading={modelsLoading}
        hasActiveFilters={hasActiveFilters}
        ownerCounts={ownerCounts}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        onModelSearchChange={handleModelSearchChange}
      />

      {/* Table */}
      <OffersTable
        offers={offers}
        isLoading={isLoading}
        totalCount={totalCount}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onShowContent={handleShowContent}
        onStartCampaign={handleStartCampaign}
      />

      {/* Content Preview Modal */}
      {selectedOffer && (
        <ContentPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={handleClosePreviewModal}
          modelName={selectedOffer.of_user_name}
          mediaUrls={selectedOffer.public_content || []}
        />
      )}

      {/* Start Campaign Modal */}
      {selectedOffer && (
        <StartCampaignModal
          isOpen={isStartCampaignModalOpen}
          onClose={handleCloseStartCampaignModal}
          offer={selectedOffer}
          onSuccess={fetchOffers}
        />
      )}
    </div>
  );
};

export default RevshareOffers;
